import type { InstrumentDetail } from './api';

/**
 * Satu baris CSV = satu INDIKATOR. Kolom dimensi diulang di tiap barisnya.
 *
 * Bentuk datar ini dipilih karena itulah yang bisa dibuka dan disunting di
 * Excel — tempat instrumen ini memang disusun. Struktur bersarang yang lebih
 * "rapi" (JSON, atau satu baris per dimensi) justru tidak bisa disunting di
 * alat yang sehari-hari dipakai SLI.
 */
export const INSTRUMENT_ALIASES: Record<string, string[]> = {
  sectionCode: ['bagian', 'section', 'kode bagian'],
  dimensionCode: ['kode dimensi', 'kode', 'dimensi kode'],
  dimensionName: ['nama dimensi', 'dimensi', 'dimension'],
  number: ['no', 'nomor', 'no.', 'number'],
  name: ['indikator', 'nama indikator', 'indicator'],
  description: ['deskripsi', 'keterangan', 'description'],
  criteria1: ['kriteria 1', 'kriteria1', 'nilai 1', 'level 1'],
  criteria2: ['kriteria 2', 'kriteria2', 'nilai 2', 'level 2'],
  criteria3: ['kriteria 3', 'kriteria3', 'nilai 3', 'level 3'],
  criteria4: ['kriteria 4', 'kriteria4', 'nilai 4', 'level 4'],
  weight: ['bobot', 'bobot %', 'weight'],
  priorityYear: ['prioritas tahun', 'prioritas', 'tahun prioritas'],
  mov: ['mov', 'bukti', 'cara memperoleh nilai', 'means of verification'],
};

const HEADERS = [
  'Bagian',
  'Kode Dimensi',
  'Nama Dimensi',
  'No',
  'Indikator',
  'Deskripsi',
  'Kriteria 1',
  'Kriteria 2',
  'Kriteria 3',
  'Kriteria 4',
  'Bobot',
  'Prioritas Tahun',
  'MoV',
];

/** Membungkus sel yang mengandung koma, kutip, atau baris baru. */
function cell(value: string | number | null | undefined): string {
  const s = value == null ? '' : String(value);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export const INSTRUMENT_TEMPLATE =
  HEADERS.join(',') +
  '\n' +
  [
    'A',
    'D1',
    'Kepemimpinan & Tata Kelola TBM',
    '1',
    'Kapasitas Pengelola TBM',
    'Kemampuan pengelola menjalankan fungsi manajemen TBM.',
    'Masih bergantung pada satu orang.',
    'Sudah ada pembagian peran dasar.',
    'Menjalankan tugas sesuai perannya.',
    'Bekerja sebagai tim dan melakukan regenerasi.',
    '5',
    '1',
    'Portofolio Pengelolaan TBM; Hasil wawancara',
  ]
    .map(cell)
    .join(',') +
  '\n';

/**
 * Mengubah struktur yang sedang berlaku menjadi CSV.
 *
 * Inilah alur yang sebenarnya dipakai: unduh keadaan sekarang → sunting massal
 * di Excel → unggah balik. Menyusun 16 indikator dengan 64 kriteria dari
 * templat kosong hampir tidak pernah terjadi.
 */
export function structureToCsv(data: InstrumentDetail): string {
  const rows: string[] = [HEADERS.join(',')];

  for (const section of data.sections) {
    for (const dimension of section.dimensions) {
      for (const indicator of dimension.indicators) {
        const byScore = (score: number) =>
          indicator.criteria.find((c) => c.score === score)?.description ?? '';

        rows.push(
          [
            section.code,
            dimension.code,
            dimension.name,
            indicator.number,
            indicator.name,
            indicator.description ?? '',
            byScore(1),
            byScore(2),
            byScore(3),
            byScore(4),
            indicator.weight,
            indicator.priorityYear ?? '',
            indicator.mov.map((m) => m.label).join('; '),
          ]
            .map(cell)
            .join(','),
        );
      }
    }
  }

  return rows.join('\n') + '\n';
}
